import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, ConfirmDialog, LayerContextProvider, layers, Caption1, Body2, Label } from "@activecollab/components";
import classNames from "classnames";
import { Formik, Form, Field } from "formik";
import { List } from "immutable";
import PropTypes from "prop-types";
import LabelMap from "~/records/record.label_map.jsx";
import TaskListMap from "~/records/record.task_list_map.jsx";
import { getIsMultipleAssigneesEnabled } from "~/store/selectors/selector.configOptions.jsx";
import { keyCodes } from "~/utils/key_codes.jsx";
import { SelectTaskLabels } from "./SelectTaskLabels.js";
import TaskFormSelectDate from "./TaskFormSelectDate.jsx";
import { SubscribersPicker } from "./TaskPanel/subscribers/SubscribersPicker.js";
import AngDropFiles from "../component.ang_drop_files.jsx";
import SelectTimeEstimate from "../component.select_time_estimate.jsx";
import { CustomFormField } from "../CustomFormField.jsx";
import { FormikEffect } from "../FormikEffect.jsx";
import { useFileHandler } from "../Hooks/useFileHandler.jsx";
import { FormCheckbox } from "../ModalObjects";
import { MultiAssigneesPicker } from "../MultiAssigneesPicker/MultiAssigneesPicker.jsx";
import { AttachmentUpload } from "../select_attachments/AttachmentUpload.jsx";
import { SelectAttachments } from "../select_attachments/SelectAttachments.jsx";
import SelectContainer from "../SelectContainer/SelectContainer.js";
import SelectTaskList from "../SelectTaskList.js";
import { SubscribersAvatarList } from "../SubscribersAvatarList/SubscribersAvatarList.js";
import { TimeEstimate } from "../TimeEstimate/TimeEstimate.jsx";
import VisualEditorContainer from "../VisualEditor/VisualEditorContainer.jsx";

const TaskForm = ({
  id,
  projectId,
  users,
  attachments,
  taskLists,
  submitButtonLabel,
  canSeeTimeEstimates,
  name,
  description,
  bodyMode,
  taskListId,
  assigneeId,
  startOn,
  dueOn,
  hiddenFromClients,
  important,
  estimate,
  jobTypeId,
  subscribers,
  projectMembers,
  attachUploadedFiles,
  dropAttachedFiles,
  labels,
  labelNames: defaultLabelNames,
  labelIds: defaultLabelIds,
  onCancel,
  onChange,
  hasDraft,
  onSubmit,
  hideFromClients,
  isLoggedUserClient,
  shouldOverflow,
  assigneeIds,
}) => {
  const inputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [members, setMembers] = useState(() =>
    hiddenFromClients ? users.filter((user) => user.class !== "Client") : users
  );
  const multiAssigneesEnabled = useSelector(getIsMultipleAssigneesEnabled);

  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler();

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const labelIds = useMemo(() => {
    return defaultLabelNames?.size
      ? labels
          .toList()
          .filter((l) => defaultLabelNames.includes(l.name))
          .map((l) => l.id)
      : defaultLabelIds;
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  const labelNames = useMemo(() => {
    return defaultLabelNames && defaultLabelNames.size
      ? defaultLabelNames
      : labels
          .toList()
          .filter((l) => labelIds.includes(l.id))
          .map((l) => l.name);
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  const possibleSubscribers = useMemo(() => {
    return projectMembers.map((m) => m.id);
  }, [projectMembers]);

  const handleConfirmModalSubmit = useCallback(() => {
    setShowConfirmationModal(false);
    if (typeof onCancel === "function") {
      onCancel();
    }
  }, [onCancel]);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleSubmit = useCallback(
    (values) => {
      const {
        assigneeId,
        description,
        hiddenFromClients,
        important,
        name,
        dates,
        taskListId,
        timeEstimate,
        subscribers,
        attachments,
        labels,
        assigneeIds,
      } = values;

      const clientAssignee = users.find((user) => user.id === assigneeId && user.class === "Client");

      const attributes = {
        assignee_id: (hiddenFromClients && clientAssignee) || multiAssigneesEnabled ? undefined : assigneeId,
        body: description,
        is_hidden_from_clients: !!hiddenFromClients,
        is_important: !!important,
        name,
        reset_attachments: true,
        reset_subscribers: true,
        due_on: dates.dueOn,
        start_on: dates.startOn,
        subscribers,
        subtasks: Array[0],
        task_list_id: taskListId,
        job_type_id: timeEstimate.job_type_id,
        estimate: timeEstimate.estimate,
        assignee_ids: multiAssigneesEnabled ? assigneeIds : undefined,
      };

      if (attachments.filesToAttach) {
        attributes.attach_uploaded_files = attachments.filesToAttach;
      }

      if (attachments.filesToDrop) {
        attributes.drop_attached_files = attachments.filesToDrop;
      }

      if (labels.labelNames.size > 0) {
        attributes.labels = labels.labelNames.toArray();
      }

      if (labels.labelNames.size === 0 && labels.labelIds.size > 0) {
        attributes.labels = labels.labelIds.toArray();
      }

      if (labels.labelNames.size === 0 && labels.labelIds.size === 0) {
        attributes.labels = [];
      }

      onSubmit(attributes);
    },
    [multiAssigneesEnabled, onSubmit, users]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (submitButtonRef.current && event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submitButtonRef.current.click();
      }
      if (event.key === keyCodes.escape) {
        event.preventDefault();
        if (hasDraft) {
          setShowConfirmationModal(true);
        } else if (typeof onCancel === "function") {
          onCancel();
        }
      }
    },
    [onCancel, hasDraft]
  );

  const handleCancel = useCallback(() => {
    if (hasDraft) {
      setShowConfirmationModal(true);
    } else if (typeof onCancel === "function") {
      onCancel();
    }
  }, [hasDraft, onCancel]);

  const saveFormDraft = useCallback(
    (prevFormState, nextFormState) => {
      if (typeof onChange === "function") {
        if (prevFormState.hiddenFromClients !== nextFormState.hiddenFromClients) {
          setMembers(nextFormState.hiddenFromClients ? users.filter((user) => user.class !== "Client") : users);
        }
        if (nextFormState.hiddenFromClients) {
          nextFormState.subscribers = nextFormState.subscribers.filter((subscriberId) =>
            projectMembers.find((member) => member.id === subscriberId && member.class !== "Client")
          );
        }
        const { attachments, dates, labels, timeEstimate, assigneeId, ...rest } = nextFormState;
        onChange({
          attachUploadedFiles: attachments.filesToAttach,
          dropAttachedFiles: attachments.filesToDrop,
          ...labels,
          ...dates,
          ...timeEstimate,
          ...rest,
        });
      }
    },
    [onChange, projectMembers, users]
  );

  const handleKeyPressEditor = useCallback((event) => {
    if (event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  }, []);

  const handleValidateForm = useCallback(
    (values) => {
      let errors = {};
      const nameLength = values.name.length;
      if (!nameLength) {
        errors.name = t("Required");
      } else if (nameLength > 150) {
        errors.name = t("Maximum of 150 characters exceeded.");
      }
      return errors;
    },
    [t]
  );

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  const taskListsOptions = useMemo(
    () =>
      taskLists
        .toList()
        .toJS()
        .map((label) => ({
          id: label.id,
          name: label.name,
        })),
    [taskLists]
  );

  return (
    <Formik
      initialValues={{
        name,
        description,
        taskListId,
        assigneeId,
        dates: {
          startOn,
          dueOn,
        },
        subscribers: subscribers,
        labels: {
          labelIds,
          labelNames,
        },
        timeEstimate: {
          estimate,
          job_type_id: jobTypeId,
        },
        hiddenFromClients: isLoggedUserClient ? false : hiddenFromClients,
        important,
        attachments: {
          filesToAttach: attachUploadedFiles,
          filesToDrop: dropAttachedFiles,
        },
        assigneeIds,
      }}
      validate={handleValidateForm}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, isValid, setFieldValue }) => (
        <>
          <Form
            onKeyDown={handleKeyPress}
            className="task_edit_mode tw-max-h-full tw-flex tw-flex-col"
            data-testid="task-form"
          >
            <FormikEffect onChange={saveFormDraft} />
            <div
              className={classNames(
                "tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 tw-max-h-full tw-flex-1 sm:tw-pr-3 lg:tw-pr-0",
                { "tw-overflow-y-auto": shouldOverflow }
              )}
            >
              <div className="tw-flex-1 tw-rounded-md tw-max-w-[660px] tw-bg-page-paper-main tw-flex tw-flex-col">
                <div className="tw-flex tw-flex-col tw-h-full">
                  <Field name="name">
                    {({ field }) => (
                      <input
                        ref={inputRef}
                        id="taskNameInput"
                        type="text"
                        tabIndex={0}
                        maxLength={150}
                        placeholder={`${t("Task name")} *`}
                        data-testid="project-task-name-input"
                        className="tw-text-lg tw-font-bold tw-px-6 tw-py-4 tw-outline-none tw-w-full tw-bg-transparent tw-text-theme-900 tw-border-border-primary tw-border-solid tw-border tw-rounded-t-md tw-border-b-0"
                        {...field}
                      />
                    )}
                  </Field>
                  <div className="tw-border-border-primary tw-border-solid tw-border tw-flex-1 tw-border-t-transparent task_form_description_container">
                    <CustomFormField name="description">
                      {({ field }) => (
                        <VisualEditorContainer
                          projectId={projectId}
                          parentType="Task"
                          parentId={id}
                          content={field.value}
                          bodyMode={bodyMode}
                          onKeyDown={handleKeyPressEditor}
                          onChange={field.onChange}
                          placeholder={t(`Write a description or press "/" for commands`)}
                        />
                      )}
                    </CustomFormField>
                  </div>
                  <div className="tw-px-6 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-border-t-transparent">
                    <CustomFormField name="attachments">
                      {({ field }) => (
                        <SelectAttachments
                          {...fileHandlerRest}
                          uploading={uploading}
                          attachmentIds={attachments}
                          filesToAttach={field.value.filesToAttach}
                          filesToDrop={field.value.filesToDrop}
                          onChange={field.onChange}
                          onOpenFileWindow={handleOpenFileWindow}
                        />
                      )}
                    </CustomFormField>
                  </div>
                  <div className="tw-px-6 tw-py-5 tw-h-[66px] tw-flex tw-items-center tw-border tw-border-border-primary tw-border-solid tw-border-t-transparent tw-rounded-b-md">
                    <Body2
                      weight="bold"
                      className="tw-truncate tw-w-[100px] tw-mr-1"
                    >
                      {t("Subscribers")}
                    </Body2>
                    <CustomFormField name="subscribers">
                      {({ field }) => {
                        return (
                          <SubscribersPicker
                            onChange={field.onChange}
                            userIds={possibleSubscribers}
                            hideClients={hiddenFromClients}
                            selected={field.value}
                            target={
                              <SubscribersAvatarList
                                taskId={id}
                                subscriberIds={field.value}
                              />
                            }
                          />
                        );
                      }}
                    </CustomFormField>
                  </div>
                </div>
                <AngDropFiles onDrop={handleUpload} />
              </div>
              <div className="tw-w-full tw-max-w-[280px] lg:tw-max-w-[328px] lg:tw-px-6 tw-mt-6 lg:tw-mt-0">
                {taskListsOptions?.length > 1 && taskListId ? (
                  <div className="tw-mb-6">
                    <Label
                      size="small"
                      className="tw-mb-1 tw-block"
                    >
                      {t("Task List")}
                    </Label>
                    <CustomFormField name="taskListId">
                      {({ field }) => (
                        <SelectTaskList
                          projectId={projectId}
                          options={taskListsOptions}
                          selected={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    </CustomFormField>
                  </div>
                ) : null}

                {!multiAssigneesEnabled ? (
                  <div className="tw-mb-6">
                    <Label
                      size="small"
                      className="tw-mb-1 tw-block"
                    >
                      {t("Assignee")}
                    </Label>
                    <CustomFormField name="assigneeId">
                      {({ field }) => (
                        <SelectContainer
                          options={members}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder={t("Choose an assignee")}
                          defaultValue={t("No Assignee...")}
                          className="tw-w-full"
                        />
                      )}
                    </CustomFormField>
                  </div>
                ) : (
                  <div className="tw-bg-[var(--input-background-color)] tw-px-3 tw-py-2 tw-border tw-border-solid tw-border-[var(--color-theme-500)] tw-rounded-[8px] tw-mb-6">
                    <div className="tw-flex tw-flex-col">
                      <div tabIndex="-1">
                        <div className="tw-flex tw-justify-between tw-items-center">
                          <Label size="small">{t("Assignees:")}</Label>
                          <CustomFormField name="assigneeIds">
                            {({ field }) => (
                              <MultiAssigneesPicker
                                projectId={projectId}
                                hiddenFromClients={hiddenFromClients}
                                assigneeIds={field.value}
                                onChange={(val) => {
                                  setFieldValue("assigneeIds", val);
                                }}
                                canEdit={true}
                              />
                            )}
                          </CustomFormField>
                        </div>
                      </div>
                      <div tabIndex="-1">
                        <CustomFormField name="timeEstimate">
                          {({ field }) => (
                            <TimeEstimate
                              canEdit={canSeeTimeEstimates}
                              estimate={field.value.estimate}
                              onChange={field.onChange}
                            />
                          )}
                        </CustomFormField>
                      </div>
                    </div>
                  </div>
                )}

                <div className="tw-mb-6">
                  <Label
                    size="small"
                    className="tw-mb-1 tw-block"
                  >
                    {t("Due On")}
                  </Label>
                  <CustomFormField name="dates">
                    {({ field }) => (
                      <TaskFormSelectDate
                        userId={values.assigneeId}
                        from={field.value.startOn}
                        to={field.value.dueOn}
                        onChange={field.onChange}
                      />
                    )}
                  </CustomFormField>
                </div>

                {canSeeTimeEstimates && !multiAssigneesEnabled && (
                  <div className="tw-mb-6">
                    <Caption1
                      weight="bold"
                      className="tw-mb-1 tw-block"
                    >
                      {t("Time Estimation")}
                    </Caption1>
                    <CustomFormField name="timeEstimate">
                      {({ field }) => (
                        <SelectTimeEstimate
                          estimate={field.value.estimate}
                          jobTypeId={field.value.job_type_id}
                          onChange={field.onChange}
                          setTabIndex={0}
                        />
                      )}
                    </CustomFormField>
                  </div>
                )}

                <div className="tw-mb-6">
                  <Label
                    size="small"
                    className="tw-mb-1 tw-block"
                  >
                    {t("Labels")}
                  </Label>
                  <CustomFormField name="labels">
                    {({ field }) => {
                      return (
                        <SelectTaskLabels
                          projectId={projectId}
                          labels={labels}
                          selected={field.value.labelIds}
                          onChange={field.onChange}
                        />
                      );
                    }}
                  </CustomFormField>
                </div>

                {hideFromClients && !isLoggedUserClient ? (
                  <CustomFormField name="hiddenFromClients">
                    {({ field }) => (
                      <FormCheckbox
                        id="task_form_hidden_from_clients"
                        checked={field.value}
                        onChange={field.onChange}
                        className="form_field tw-my-2"
                        text={t("Hidden from clients")}
                      />
                    )}
                  </CustomFormField>
                ) : null}
                <CustomFormField name="important">
                  {({ field }) => (
                    <FormCheckbox
                      id="task_form_high_priority"
                      checked={field.value}
                      onChange={field.onChange}
                      className="form_field tw-my-2"
                      text={t("High priority")}
                    />
                  )}
                </CustomFormField>
              </div>
            </div>
            <div className="tw-pt-4">
              <Button
                ref={submitButtonRef}
                type="submit"
                variant="primary"
                disabled={isSubmitting || !isValid || uploading}
                tabIndex={0}
                className="tw-mr-3"
                data-qa-id="add-form-button"
              >
                {uploading ? `${t("Uploading")}...` : submitButtonLabel}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                tabIndex={0}
                data-qa-id="cancel-task-button"
              >
                {t("Cancel")}
              </Button>
            </div>
          </Form>
          <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
            <ConfirmDialog
              open={showConfirmationModal}
              dialogTitle={t("Discard changes?")}
              dialogContent={t("All unsaved changes will be lost.")}
              confirmBtnText={t("Discard")}
              cancelBtnText={t("Cancel")}
              onConfirm={handleConfirmModalSubmit}
              onCancel={handleCloseConfirmationModal}
            />
          </LayerContextProvider>
          <AttachmentUpload
            ref={fileInputRef}
            multiple
            onUpload={onUpload}
          />
        </>
      )}
    </Formik>
  );
};

TaskForm.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  projectId: PropTypes.number.isRequired,
  taskListId: PropTypes.number,
  description: PropTypes.string,
  attachments: PropTypes.instanceOf(List),
  subscribers: PropTypes.array,
  assigneeId: PropTypes.number,
  labelIds: PropTypes.instanceOf(List),
  labelNames: PropTypes.instanceOf(List),
  startOn: PropTypes.number,
  dueOn: PropTypes.number,
  hiddenFromClients: PropTypes.bool,
  isLoggedUserClient: PropTypes.bool,
  important: PropTypes.bool,
  estimate: PropTypes.string,
  jobTypeId: PropTypes.number,
  users: PropTypes.array.isRequired,
  projectMembers: PropTypes.array,
  companies: PropTypes.array.isRequired,
  teams: PropTypes.array.isRequired,
  labels: PropTypes.instanceOf(LabelMap).isRequired,
  taskLists: PropTypes.instanceOf(TaskListMap),
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  pendingSubmit: PropTypes.bool,
  canSeeTimeEstimates: PropTypes.bool,
  attachUploadedFiles: PropTypes.arrayOf(PropTypes.string),
  dropAttachedFiles: PropTypes.arrayOf(PropTypes.number),
  hasDraft: PropTypes.bool,
  hideFromClients: PropTypes.bool,
  bodyMode: PropTypes.string,
  submitButtonLabel: PropTypes.string,
  shouldOverflow: PropTypes.bool,
  assigneeIds: PropTypes.array,
};

TaskForm.defaultProps = {
  id: 0,
  name: ``,
  description: ``,
  attachments: List(),
  subscribers: [],
  labelIds: List(),
  hiddenFromClients: false,
  important: false,
  jobTypeId: 0,
  taskLists: new TaskListMap(),
  onCancel: () => null,
  onSubmit: () => null,
  onChange: () => null,
  onLabelsSubmit: () => {},
  pendingSubmit: false,
  canSeeTimeEstimates: false,
  attachUploadedFiles: [],
  dropAttachedFiles: [],
  projectMembers: [],
  hasDraft: false,
  assigneeIds: [],
};

export default TaskForm;
